import {
  Card,
  Header,
  IconExternalLink,
  PrimaryButton,
  Space,
  Text,
} from '@procurify/ui'

import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import { ConversationCheckoutOrderSummaryVendor } from './ConversationCheckoutOrderSummaryVendor'
import { type IConversationOrderSummaryDelivery } from './ConversationOrderSummaryDelivery'
import { ConversationOrderSummarySource } from './ConversationOrderSummarySource'
import { ConversationOrderSummaryTotals } from './ConversationOrderSummaryTotals'
import messages from './messages'
import { ConversationOrderSummaryCardStyled } from './styles/ConversationOrderSummaryCardStyled'
import {
  type ConversationCartAction,
  type ConversationCartAddress,
  type ConversationCartCharge,
  type ConversationCartPaymentMethod,
  type ConversationVendor,
} from '../../value-objects/ConversationTurnItem'
import { ConversationCartActionIcons } from '../../value-objects/enums/ConversationCartActionIconEnum'
import { VendorLogoBadgeMarkHeights } from '../VendorLogoBadge'

export interface IConversationCheckoutOrderSummaryGroup {
  vendor?: ConversationVendor
  shipTo?: ConversationCartAddress
  billTo?: ConversationCartAddress
  paymentMethod?: ConversationCartPaymentMethod
  deliveries: IConversationOrderSummaryDelivery[]
}

export interface IConversationCheckoutOrderSummaryProps {
  groups: IConversationCheckoutOrderSummaryGroup[]
  total: Nullable<number>
  currency: CurrencyName
  charges?: ConversationCartCharge[]
  disabled?: boolean
  isContinueLoading?: boolean
  totalLabel?: string
  continueAction?: ConversationCartAction
  disclaimer?: string
  onContinue: () => void
  onSendMessage: (message: string) => void
}

export const ConversationCheckoutOrderSummary = ({
  groups,
  total,
  currency,
  charges,
  disabled,
  isContinueLoading,
  totalLabel,
  continueAction,
  disclaimer,
  onContinue,
  onSendMessage,
}: IConversationCheckoutOrderSummaryProps) => {
  const intl = useProcurifyIntl()
  const isSingleVendor = groups.length === 1
  const headingVendor = isSingleVendor ? groups[0].vendor : undefined
  const isExternalCheckout =
    continueAction?.icon === ConversationCartActionIcons.EXTERNAL

  return (
    <ConversationOrderSummaryCardStyled>
      <Card.Content>
        <Space direction='vertical' size='medium'>
          <Space direction='horizontal' size='small' align='end'>
            <Header as='h5'>
              {intl.formatMessage(messages.checkoutHeading)}
            </Header>
            {headingVendor && (
              <ConversationOrderSummarySource
                vendor={headingVendor}
                outlined={false}
                markHeight={VendorLogoBadgeMarkHeights.COMPACT}
              />
            )}
          </Space>

          {groups.map((group, index) => (
            <ConversationCheckoutOrderSummaryVendor
              key={group.vendor?.id ?? index}
              group={group}
              currency={currency}
              showSource={!isSingleVendor}
              disabled={disabled}
              onSendMessage={onSendMessage}
            />
          ))}

          <ConversationOrderSummaryTotals
            charges={charges}
            total={total}
            currency={currency}
            totalLabel={totalLabel ?? intl.formatMessage(messages.total)}
          />

          <Space direction='vertical' size='small'>
            <PrimaryButton
              analyticsId='conversation-confirm-order-button'
              showIconOnEnd={isExternalCheckout}
              iconComponent={
                isExternalCheckout ? <IconExternalLink /> : undefined
              }
              disabled={disabled}
              isLoading={isContinueLoading}
              onClick={onContinue}
            >
              {continueAction?.text ??
                intl.formatMessage(messages.continueButton)}
            </PrimaryButton>

            {disclaimer && <Text color='textSecondary'>{disclaimer}</Text>}
          </Space>
        </Space>
      </Card.Content>
    </ConversationOrderSummaryCardStyled>
  )
}
